import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContactEmail, ContactPhone, Loading, Section, Text } from '@tradingblock/components';
import { ProgressBar } from 'react-bootstrap';
import useSWR from 'swr';
import { PageTitle } from '../components';
import { ApplicationLoader } from '../components/application';
import { getApplicationStepUrl, getForcedOffRampUrl } from '../services';
import { isUserAuthenticated, useData, useStateSelector } from '../state/selectors';
import { BooleanToggleType, ClearerType, DefaultApplicationModel, SiteIds } from '../types';
import { useDispatcher } from '../components/hooks';
import { ApplicationActions } from '../state/actions';
import { UploadedDocuments } from '../components/values';
import { GetAccountDetailsUrl, getRequestedParticipantsDocuments } from '@tradingblock/api';
import { AccountApplicationState, AccountStatus, AccountStatusesEnum, ApplicationType } from '@tradingblock/types';
import { useAccountApplicationsWebSocket } from '../hooks/useAccountApplicationsWebSocket';
import { useExpiringTokensWebSocket } from '../hooks/useExpiringTokensWebSocket';
import { WebSocketStatus, WebSocketStatuses } from '../types/WebSocket';
import DocumentUpload from '../components/fields/DocumentUpload';
import { useApi } from '../context';
import config from '../config';
import FundYourAccount from '../components/fund-your-account/FundYourAccount';

const StatusPage: React.FC = () => {
  const isRnWebView = window.isRnWebView;
  const siteGrpParam = process.env.REACT_APP_TB_SITE_GROUP;

  const dispatch = useDispatcher();

  const isAuthenticated = useStateSelector(isUserAuthenticated);
  const application = useData(d => d.data.application);
  const applicationStatus = useData(d => d.data.status);
  const accountApplicationWebSocketStatus = useStateSelector(s => s.accountApplicationWebSocket.status);
  const isFetchingStatus = useData(d => d.data.isFetchingStatus);
  const { isSaving, saveType } = useData(d => d.data.saveInfo);
  const hasReceivedAccountApplicationUpdateMessage = useStateSelector(
    s => s.accountApplicationWebSocket.hasReceivedAccountApplicationUpdateMessage
  );
  const siteGrp = useData(d => d.ui.siteGrp);

  const [disabled, setDisabled] = useState(false);
  const [currentProgressBarValue, setCurrentProgressBarValue] = useState<number>(0);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);

  const {
    subscribeToAccountApplicationUpdates,
    unsubscribeFromAccountApplicationUpdates,
  } = useAccountApplicationsWebSocket();

  const { unsubscribeExpiringTokens } = useExpiringTokensWebSocket();

  const successfullySubscribedToAccountUpdates = useRef<boolean>(false);
  const applicationStatusRequestInterval = useRef<NodeJS.Timeout | null>(null);
  const prevWebSocketStatus = useRef<WebSocketStatus>(accountApplicationWebSocketStatus);

  const rqdAccountApplicationStatuses = [
    AccountApplicationState.PendingSubmit,
    AccountApplicationState.Pending,
    AccountApplicationState.Suspended,
    AccountApplicationState.Complete,
  ];
  const apexAccountApplicationStatuses = [
    AccountApplicationState.PendingSubmit,
    AccountApplicationState.New,
    AccountApplicationState.Pending,
    AccountApplicationState.InvestigationSubmitted,
    AccountApplicationState.Suspended,
    AccountApplicationState.ActionRequired,
    AccountApplicationState.ReadyForBackOffice,
    AccountApplicationState.BackOffice,
    AccountApplicationState.AccountSetup,
    [AccountApplicationState.Complete, AccountApplicationState.Cancelled, AccountApplicationState.Rejected],
  ];

  const offRampUrl = getForcedOffRampUrl(application);
  const isOffline = offRampUrl === '/apply/offline';
  const isPartialEntitySubmission = application && application.isPartialEntitySubmission;
  const { accountId, clearer } = application || DefaultApplicationModel;
  const { state, investigations, applications } = applicationStatus || {
    state: undefined,
    investigations: [],
    applications: [],
  };
  const apiToken = useStateSelector(s => s.ui.apiToken);

  const api = useApi();

  type ApplicationStatusBasedOnAccountStatus = 'complete' | 'processing' | 'error';
  enum ApplicationStatusesBasedOnAccountStatus {
    complete = 'complete',
    processing = 'processing',
    error = 'error',
  }

  const applicableApplicationStatusBasedOnAccountStatus: ApplicationStatusBasedOnAccountStatus | null = useMemo(() => {
    if (!accountStatus) {
      return null;
    }

    if (
      accountStatus === AccountStatusesEnum.ProspectApplicationStarted ||
      accountStatus === AccountStatusesEnum.ProspectApplicationCompleted ||
      accountStatus === AccountStatusesEnum.ProcessingItemsMissing ||
      accountStatus === AccountStatusesEnum.Prospect ||
      accountStatus === AccountStatusesEnum.Processing
    ) {
      return ApplicationStatusesBasedOnAccountStatus.processing;
    } else if (
      accountStatus === AccountStatusesEnum.ClosedNeverFunded ||
      accountStatus === AccountStatusesEnum.ProspectApplicationExpired ||
      accountStatus === AccountStatusesEnum.Rejected ||
      accountStatus === AccountStatusesEnum.Closed
    ) {
      return ApplicationStatusesBasedOnAccountStatus.error;
    }

    return ApplicationStatusesBasedOnAccountStatus.complete;
  }, [accountStatus]);

  const {
    isLoading: isLoadingAccountDetailsData,
    data: getAccountDetailsResponse,
    error: errorGettingAccountDetailsData,
  } = useSWR(
    isPartialEntitySubmission &&
      accountId &&
      isAuthenticated &&
      (!accountStatus ||
        (accountStatus &&
          applicableApplicationStatusBasedOnAccountStatus === ApplicationStatusesBasedOnAccountStatus.processing))
      ? `${config.tradingApi}/${GetAccountDetailsUrl}?accountId=${accountId}`
      : null,
    () => {
      if (accountId) {
        return api.accounts.details(accountId);
      }

      return null;
    },
    { revalidateOnFocus: false, refreshInterval: 600000 }
  );

  // Show loader during initial loading and when transitioning from form submission
  // This includes when the page first loads, when status is being fetched,
  // and when we're still in the submission process
  const isInitialLoading =
    isFetchingStatus ||
    isLoadingAccountDetailsData ||
    (isSaving && saveType === 'complete') ||
    (!applicationStatus && application && accountId);

  const prevAccountApplicationStatus = useRef<AccountApplicationState | undefined>(state);

  const determineCurrentProgressBarValue = () => {
    const accountApplicationStatuses =
      clearer === ClearerType.RQD ? rqdAccountApplicationStatuses : apexAccountApplicationStatuses;
    const totalAccountApplicationStatuses = accountApplicationStatuses.length;

    if (isPartialEntitySubmission && accountStatus) {
      if (applicableApplicationStatusBasedOnAccountStatus === ApplicationStatusesBasedOnAccountStatus.processing) {
        return 50;
      }

      return 100;
    }

    if (isPartialEntitySubmission && !accountStatus) {
      return 0;
    }

    if (applicationStatus === null || !application || !accountId) {
      return 0;
    } else if (state && state !== AccountApplicationState.Error) {
      const currentAccountApplicationIndex = accountApplicationStatuses.findIndex(accountApplicationStatusStep => {
        if (!prevAccountApplicationStatus.current) {
          return -1;
        }

        if (typeof accountApplicationStatusStep === 'object') {
          return accountApplicationStatusStep.includes(prevAccountApplicationStatus.current);
        } else {
          return prevAccountApplicationStatus.current === accountApplicationStatusStep;
        }
      });

      const newAccountApplicationIndex = accountApplicationStatuses.findIndex(accountApplicationStatusStep =>
        typeof accountApplicationStatusStep === 'object'
          ? accountApplicationStatusStep.includes(state)
          : state === accountApplicationStatusStep
      );

      if (
        currentAccountApplicationIndex >= 0 &&
        newAccountApplicationIndex >= 0 &&
        newAccountApplicationIndex < currentAccountApplicationIndex
      ) {
        return currentProgressBarValue;
      }

      if (
        newAccountApplicationIndex === -1 ||
        (clearer === ClearerType.Apex &&
          apexAccountApplicationStatuses[newAccountApplicationIndex] === AccountApplicationState.ActionRequired)
      ) {
        return (
          (((currentProgressBarValue / 100) * totalAccountApplicationStatuses + 1) / totalAccountApplicationStatuses) *
          100
        );
      }

      return ((newAccountApplicationIndex + 1) / totalAccountApplicationStatuses) * 100;
    } else {
      return currentProgressBarValue;
    }
  };

  useEffect(() => {
    setCurrentProgressBarValue(determineCurrentProgressBarValue());
  }, [
    state,
    clearer,
    applicationStatus,
    application,
    accountId,
    accountStatus,
    applicableApplicationStatusBasedOnAccountStatus,
    isPartialEntitySubmission,
  ]);

  useEffect(() => {
    prevWebSocketStatus.current = accountApplicationWebSocketStatus;
    if (
      accountApplicationWebSocketStatus !== WebSocketStatuses.OPEN &&
      successfullySubscribedToAccountUpdates.current
    ) {
      successfullySubscribedToAccountUpdates.current = false;
    }
  }, [accountApplicationWebSocketStatus]);

  // Reduce and union both investigations and applications requested documents
  const requestedParticipantsDocuments = useMemo(() => {
    return getRequestedParticipantsDocuments(investigations, applications);
  }, [investigations, applications]);

  // boolean to check if we have any requested documents
  const areDocumentsRequested = useMemo(() => {
    return requestedParticipantsDocuments.some(
      participant =>
        participant.requestedCatagories.investigations.length > 0 ||
        participant.requestedCatagories.application.length > 0
    );
  }, [requestedParticipantsDocuments]);

  useEffect(() => {
    if (
      accountId &&
      !successfullySubscribedToAccountUpdates.current &&
      accountApplicationWebSocketStatus === WebSocketStatuses.OPEN &&
      !isOffline &&
      !isPartialEntitySubmission &&
      isAuthenticated
    ) {
      const response = subscribeToAccountApplicationUpdates(accountId, apiToken || '');

      if (response.success) {
        successfullySubscribedToAccountUpdates.current = true;
      }
    }
  }, [accountId, isAuthenticated, accountApplicationWebSocketStatus, isOffline, isPartialEntitySubmission]);

  useEffect(() => {
    if (isAuthenticated && accountId && !isOffline && !isPartialEntitySubmission) {
      dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
    }
  }, [isAuthenticated, accountId, isOffline, isPartialEntitySubmission]);

  useEffect(() => {
    return () => {
      if (applicationStatusRequestInterval.current) {
        clearInterval(applicationStatusRequestInterval.current);
      }

      if (successfullySubscribedToAccountUpdates) {
        if (accountId) {
          unsubscribeFromAccountApplicationUpdates(accountId, apiToken || '');
          unsubscribeExpiringTokens();
        }
      }
    };
  }, []);

  useEffect(() => {
    if (
      (((accountApplicationWebSocketStatus === WebSocketStatuses.CLOSED ||
        accountApplicationWebSocketStatus === WebSocketStatuses.ERROR) &&
        prevWebSocketStatus.current === WebSocketStatuses.OPEN) ||
        accountApplicationWebSocketStatus === WebSocketStatuses.ERROR ||
        applicationStatus === null ||
        !hasReceivedAccountApplicationUpdateMessage) &&
      accountId &&
      !isOffline &&
      !isPartialEntitySubmission
    ) {
      if (applicationStatusRequestInterval.current) {
        clearInterval(applicationStatusRequestInterval.current);
      }

      if (isAuthenticated && !areDocumentsRequested) {
        applicationStatusRequestInterval.current = setInterval(
          () => {
            dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
          },
          applicationStatus === null ? 60000 : 600000
        );
      }
    } else {
      if (applicationStatusRequestInterval.current) {
        clearInterval(applicationStatusRequestInterval.current);
      }
    }
  }, [
    accountApplicationWebSocketStatus,
    accountId,
    isAuthenticated,
    areDocumentsRequested,
    applicationStatus,
    hasReceivedAccountApplicationUpdateMessage,
    isOffline,
    isPartialEntitySubmission,
  ]);

  useEffect(() => {
    if (state === AccountApplicationState.Complete) {
      if (applicationStatusRequestInterval.current) {
        clearInterval(applicationStatusRequestInterval.current);
      }

      if (successfullySubscribedToAccountUpdates && accountId) {
        unsubscribeFromAccountApplicationUpdates(accountId, apiToken);
        unsubscribeExpiringTokens();
      }
    }
  }, [state, accountId, successfullySubscribedToAccountUpdates]);

  useEffect(() => {
    prevAccountApplicationStatus.current = state;
  }, [state]);

  useEffect(() => {
    if (
      getAccountDetailsResponse &&
      getAccountDetailsResponse.responseCode === 0 &&
      getAccountDetailsResponse.payload &&
      getAccountDetailsResponse.payload.accountStatus
    ) {
      setAccountStatus(getAccountDetailsResponse.payload.accountStatus as AccountStatus);
    }
  }, [getAccountDetailsResponse]);

  return (
    <ApplicationLoader defaultRedirect="/">
      <PageTitle page="status" />

      <Section>
        <div className="step-title">
          <h1>
            <Text id="title" page="status" />
          </h1>
        </div>
      </Section>

      {((accountStatus &&
        applicableApplicationStatusBasedOnAccountStatus !== ApplicationStatusesBasedOnAccountStatus.complete) ||
        (state && state !== AccountApplicationState.Complete) ||
        isOffline) &&
        isAuthenticated && (
          <Section>
            <h3>
              <Text id={`${isOffline || isPartialEntitySubmission ? 'offline-' : ''}subTitle`} page="status" />
            </h3>
            <p className="mb-0">
              <Text
                id={`${isOffline || isPartialEntitySubmission ? 'offline-' : ''}subTitle-description`}
                page="status"
              />
            </p>
          </Section>
        )}

      {accountApplicationWebSocketStatus !== WebSocketStatuses.OPEN && (
        <Section>
          <div className="d-flex justify-content-center align-items-center my-3">
            <strong>
              <Text id="errorUpdatingApplicationStatus" page="status" />
            </strong>
          </div>
        </Section>
      )}

      <Section id={isOffline ? 'status-page-main-content-section-unset' : ''}>
        {isAuthenticated ? (
          <>
            {!isOffline && <ProgressBar min={0} max={100} now={currentProgressBarValue} />}

            {isFetchingStatus ||
              (isLoadingAccountDetailsData && (
                <div className="d-flex justify-content-center align-items-center my-4">
                  <Loading />
                </div>
              ))}

            {!isLoadingAccountDetailsData && isPartialEntitySubmission && accountStatus && (
              <>
                {applicableApplicationStatusBasedOnAccountStatus ===
                  ApplicationStatusesBasedOnAccountStatus.processing && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id="state.PartialEntitySubmission" page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="pageWillUpdate" page="status" />
                      </p>
                      <br />
                    </>
                  )}

                {applicableApplicationStatusBasedOnAccountStatus ===
                  ApplicationStatusesBasedOnAccountStatus.complete && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={'state.Complete'} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="activeAccount" page="status" />
                      </p>
                      <br />
                    </>
                  )}

                {applicableApplicationStatusBasedOnAccountStatus === ApplicationStatusesBasedOnAccountStatus.error && (
                  <>
                    <h4 className="my-4">
                      <Text id="current-status" page="status" />:{' '}
                      <strong>
                        <Text id={'state.Error'} page="status" />
                      </strong>
                    </h4>
                    <p>
                      <Text
                        id="unableToOpenAccount"
                        page="status"
                        data={{ emailDomain: siteGrp === SiteIds.mb ? 'moneyblock' : 'tradingblock' }}
                      />
                    </p>
                    <br />
                  </>
                )}
              </>
            )}

            {!isFetchingStatus && applicationStatus && state && (
              <>
                {(state === AccountApplicationState.InvestigationSubmitted ||
                  state === AccountApplicationState.ReadyForBackOffice ||
                  state === AccountApplicationState.BackOffice ||
                  state === AccountApplicationState.AccountSetup) && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={`state.${state}`} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <span>
                          <Text id="inProcess" page="status" /> <Text id="pageWillUpdate" page="status" />
                        </span>
                      </p>
                      <br />
                    </>
                  )}

                {(state === AccountApplicationState.PendingSubmit ||
                  state === AccountApplicationState.ActionRequired ||
                  state === AccountApplicationState.Suspended ||
                  state === AccountApplicationState.Pending ||
                  state === AccountApplicationState.New) && (
                    <>
                      {!areDocumentsRequested && (
                        <>
                          <h4 className="my-4">
                            <Text id="current-status" page="status" />:{' '}
                            <strong>
                              <Text id={`state.${state}`} page="status" />
                            </strong>
                          </h4>
                          <p>
                            <span>
                              <Text id="inProcess" page="status" /> <Text id="pageWillUpdate" page="status" />
                            </span>
                          </p>
                          <br />
                        </>
                      )}

                      {areDocumentsRequested && (
                        <>
                          <h4 className="my-4">
                            <Text id="current-status" page="status" />:{' '}
                            <strong>
                              <Text id={`state.${state}`} page="status" />
                            </strong>
                          </h4>
                          <p>
                            <Text id="moreInfoNeededDescription" page="status" />
                          </p>
                          <br />
                        </>
                      )}
                    </>
                  )}

                {(state === AccountApplicationState.Cancelled || state === AccountApplicationState.Rejected) && (
                  <>
                    <h4 className="my-4">
                      <Text id="current-status" page="status" />:{' '}
                      <strong>
                        <Text id={`state.${state}`} page="status" />
                      </strong>
                    </h4>
                    <p>
                      <Text id="accountRejected" page="status" />
                    </p>
                    <br />
                  </>
                )}

                {state === AccountApplicationState.Error && (
                  <>
                    <h4 className="my-4">
                      <Text id="current-status" page="status" />:{' '}
                      <strong>
                        <Text id={`state.${state}`} page="status" />
                      </strong>
                    </h4>
                    <p>
                      <Text id="errorStatusMessage" page="status" />
                    </p>
                    <br />
                  </>
                )}

                {state === AccountApplicationState.Complete && (
                  <>
                    <h4 className="my-4">
                      <Text id="current-status" page="status" />:{' '}
                      <strong>
                        <Text id={`state.${state}`} page="status" />
                      </strong>
                    </h4>
                    <p>
                      <Text id="activeAccount" page="status" />
                    </p>
                    <br />
                  </>
                )}

                {requestedParticipantsDocuments.map(
                  (participant, index) =>
                    (participant.requestedCatagories.application.length > 0 ||
                      participant.requestedCatagories.investigations.length > 0 ||
                      participant.uploadedDocuments.length > 0) && (
                      <div key={`${participant.firstName}-${index}`}>
                        {participant.uploadedDocuments.length > 0 && (
                          <>
                            <h4 className="my-4">
                              <Text
                                id={'documentsUploadedForPerson'}
                                page="status"
                                data={{
                                  firstName: participant.firstName,
                                  middleName: participant.middleName,
                                  lastName: participant.lastName,
                                }}
                              />
                            </h4>
                            <ul>
                              {participant.uploadedDocuments.map((uploadedDocument, index) => (
                                <React.Fragment key={'application_uploaded_document' + index}>
                                  <li>
                                    <UploadedDocuments value={uploadedDocument} />
                                  </li>
                                  <br />
                                </React.Fragment>
                              ))}
                            </ul>
                          </>
                        )}
                        {(participant.requestedCatagories.application.length > 0 ||
                          participant.requestedCatagories.investigations.length > 0) && (
                            <>
                              <h4 className="my-4">
                                <Text
                                  id={`moreInfoNeededForPerson`}
                                  page="status"
                                  data={{
                                    firstName: participant.firstName,
                                    middleName: participant.middleName,
                                    lastName: participant.lastName,
                                  }}
                                />
                              </h4>

                              <DocumentUpload
                                isRnWebView={isRnWebView}
                                dispatch={dispatch}
                                accountId={accountId}
                                disabled={disabled}
                                setDisabled={setDisabled}
                                participant={participant}
                                areDocumentsRequested={areDocumentsRequested}
                                primary={index === 0}
                              />
                            </>
                          )}
                      </div>
                    )
                )}
              </>
            )}

            {!isFetchingStatus &&
              !isLoadingAccountDetailsData &&
              (applicationStatus === null ||
                (!isOffline && !application) ||
                (!isOffline && !accountId) ||
                (isPartialEntitySubmission && (errorGettingAccountDetailsData || !accountStatus))) && (
                <div className="d-flex justify-content-center align-items-center my-4 flex-direction-col">
                  <i className="fal fa-exclamation-circle error m-1" style={{ fontSize: '3em' }} />
                  <Text id="errorLoadingApplicationStatus" page="status" />
                </div>
              )}
          </>
        ) : (
          <>
            <p>
              <strong>
                <Text id="token-expired" />
              </strong>
            </p>
            <p>
              <Link className="btn btn-block btn-outline-dark" to="/logout#next=/auth">
                <Text id="btns.sign-in-again" />
              </Link>
            </p>
          </>
        )}
      </Section>

      {isAuthenticated &&
        application &&
        accountId &&
        application.type !== ApplicationType.UGMA &&
        (((config.isBeta || config.isProduction) &&
          (!application.retirementAccount || application.retirementAccount === BooleanToggleType.No)) ||
          config.isLocal ||
          config.isDev) && (
          <Section>
            <FundYourAccount />
          </Section>
        )}

      <Section>
        <p className="mt-3">
          <strong>
            <Text id="contact.content" />
          </strong>
          <br />
          <Text
            id="contact.content"
            textKey="description"
            siteGroup={siteGrpParam}
            data={{
              email: <ContactEmail siteGroup={siteGrpParam} />,
              phone: <ContactPhone siteGroup={siteGrpParam} />,
            }}
          />
        </p>
      </Section>

      <Section>
        {isAuthenticated && (
          <p>
            <Link className="btn-unified btn-unified--primary btn-unified--full" to={getApplicationStepUrl('summary')}>
              <Text id="view-summary" page="status" />
            </Link>
          </p>
        )}
        {!isRnWebView && isAuthenticated && (
          <p>
            <Link className="btn-unified btn-unified--dark btn-unified--full" to="/logout">
              <Text id="btns.sign-out" />
            </Link>
          </p>
        )}
      </Section>
    </ApplicationLoader>
  );
};

export default StatusPage;
